import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Upload, Loader2, Trash2, ExternalLink, X, CheckCircle2, AlertTriangle, Signature, FileText, ShieldCheck, Award, Package, Scroll, Wand2, Search } from "lucide-react";

const money = (v) => (v == null ? "—" : `${(v / 1024).toFixed(1)} KB`);

/**
 * /submittals — real PDF/image binaries (SDS, OSHA certs, insurance,
 * signatures, bonds) that get merged into every proposal binder.
 */
export default function Submittals() {
  const [types, setTypes] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [scraperOpen, setScraperOpen] = useState(false);
  const [scraperState, setScraperState] = useState({ product_name: "", manufacturer: "", kind: "sds" });
  const [scraperBusy, setScraperBusy] = useState(false);
  const [scraperResult, setScraperResult] = useState(null);
  const [form, setForm] = useState({ doc_type: "sds", product_name: "", manufacturer: "", person_name: "", expires_at: "", scope_tags: "" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.get("/submittals/types"),
        api.get("/submittals"),
      ]);
      const tmap = {};
      (t.data.types || []).forEach((x) => { tmap[x.key] = x; });
      setTypes(tmap);
      setItems(l.data.items || []);
    } catch {
      /* no-op */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const submit = async () => {
    if (!file) {
      toast.error("Choose a file first");
      return;
    }
    if (!form.doc_type) {
      toast.error("Select a submittal type");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", form.doc_type);
      Object.entries(form).forEach(([k, v]) => {
        if (k === "doc_type") return;
        if (v) fd.append(k, v);
      });
      const r = await api.post("/submittals", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded "${r.data.title}"`);
      setModalOpen(false);
      setFile(null);
      setForm({ doc_type: "sds", product_name: "", manufacturer: "", person_name: "", expires_at: "", scope_tags: "" });
      if (fileRef.current) fileRef.current.value = "";
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try {
      await api.delete(`/submittals/${s.submittal_id}`);
      setItems((its) => its.filter((r) => r.submittal_id !== s.submittal_id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const runScraper = async () => {
    if (!scraperState.product_name.trim()) {
      toast.error("Type a product name first");
      return;
    }
    setScraperBusy(true);
    setScraperResult(null);
    try {
      const r = await api.post("/submittals/fetch-manufacturer", {
        product_name: scraperState.product_name.trim(),
        manufacturer: scraperState.manufacturer.trim() || null,
        kind: scraperState.kind,
      });
      setScraperResult(r.data);
      if (r.data.found) {
        toast.success(`Auto-fetched "${r.data.submittal.title}"`);
        await loadAll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fetch failed");
    } finally {
      setScraperBusy(false);
    }
  };

  // Group by high-level bucket
  const grouped = useMemo(() => {
    const buckets = {
      corporate: { label: "Corporate & Reps/Certs", icon: ShieldCheck, tone: "text-cyan-600 bg-cyan-100", items: [] },
      safety:    { label: "Safety Certifications", icon: Award,       tone: "text-emerald-600 bg-emerald-100", items: [] },
      material:  { label: "Material Submittals",   icon: Package,     tone: "text-orange-600 bg-orange-100", items: [] },
      warranty:  { label: "Warranty Documents",    icon: Scroll,      tone: "text-amber-600 bg-amber-100", items: [] },
      signature: { label: "Signatures",            icon: Signature,   tone: "text-fuchsia-600 bg-fuchsia-100", items: [] },
      other:     { label: "Other Submittals",      icon: FileText,    tone: "text-slate-600 bg-slate-100", items: [] },
    };
    items.forEach((s) => {
      const bucket = types[s.doc_type]?.for || "other";
      (buckets[bucket] || buckets.other).items.push(s);
    });
    return buckets;
  }, [items, types]);

  const daysUntil = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (isNaN(t)) return null;
    return Math.round((t - Date.now()) / 86400000);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-center gap-4 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
          <FolderOpen className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-cyan-600">// SUBMITTAL LIBRARY</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900">Real docs that ship inside every binder</h1>
        </div>
      </div>
      <p className="text-slate-600 max-w-3xl mb-8">
        Upload the actual PDFs and images your proposal binder needs — SDS sheets, OSHA cert scans, insurance
        certificates, bid/performance bond letters, product cut sheets, signed forms, and signatures. BidForge
        merges them into every binder export so evaluators see real documentation, not placeholders.
      </p>

      <div className="mb-8 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setModalOpen(true)}
          className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2"
          data-testid="submittal-add-btn"
        >
          <Upload className="w-3.5 h-3.5" />
          UPLOAD SUBMITTAL
        </button>
        <button
          onClick={() => { setScraperResult(null); setScraperOpen(true); }}
          className="text-xs font-mono font-bold tracking-widest bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 rounded-full px-5 py-2.5 inline-flex items-center gap-2"
          data-testid="submittal-scraper-btn"
        >
          <Wand2 className="w-3.5 h-3.5" />
          AUTO-FETCH FROM MANUFACTURER
        </button>
        <div className="text-xs font-mono text-slate-500">
          {items.length} document{items.length === 1 ? "" : "s"} in library
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-dashed border-slate-300 p-12 text-center" data-testid="submittal-empty">
          <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">No submittals uploaded yet</div>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Drop your first SDS sheet, OSHA cert, or insurance letter to see it embedded in your next binder export.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([key, bucket]) => {
            if (bucket.items.length === 0) return null;
            const Icon = bucket.icon;
            return (
              <div key={key} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`submittal-group-${key}`}>
                <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bucket.tone}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="font-display font-bold text-slate-900">{bucket.label}</div>
                  <span className="text-[10px] font-mono font-bold tracking-widest text-slate-500 bg-white px-2 py-0.5 rounded-full">
                    {bucket.items.length}
                  </span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {bucket.items.map((s) => {
                    const dLeft = daysUntil(s.expires_at);
                    const label = types[s.doc_type]?.label || s.doc_type;
                    return (
                      <li key={s.submittal_id} className="px-5 py-4 flex items-start gap-4" data-testid={`submittal-row-${s.submittal_id}`}>
                        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold text-slate-900 truncate">{s.title}</div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500 font-mono">
                            <span className="text-slate-700 font-bold">{label}</span>
                            {s.manufacturer && <span>Mfr: {s.manufacturer}</span>}
                            {s.product_name && <span>Product: {s.product_name}</span>}
                            {s.person_name && <span>Person: {s.person_name}</span>}
                            <span>{money(s.size)}</span>
                            {s.original_filename && <span className="truncate">{s.original_filename}</span>}
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {dLeft != null && (
                              <span
                                className={`text-[10px] font-mono font-bold tracking-widest rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${
                                  dLeft < 0
                                    ? "bg-rose-100 text-rose-700"
                                    : dLeft < 90
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-emerald-100 text-emerald-700"
                                }`}
                                data-testid={`submittal-expires-${s.submittal_id}`}
                              >
                                {dLeft < 0 ? (
                                  <AlertTriangle className="w-3 h-3" />
                                ) : (
                                  <CheckCircle2 className="w-3 h-3" />
                                )}
                                {dLeft < 0 ? `EXPIRED ${-dLeft}d AGO` : `EXPIRES IN ${dLeft}d`}
                              </span>
                            )}
                            {(s.scope_tags || []).map((tag) => (
                              <span key={tag} className="text-[10px] font-mono font-bold tracking-widest bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/submittals/${s.submittal_id}/file?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-slate-400 hover:text-cyan-600 p-1.5"
                            title="Open"
                            data-testid={`submittal-open-${s.submittal_id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => remove(s)}
                            className="text-slate-300 hover:text-rose-500 p-1.5"
                            title="Delete"
                            data-testid={`submittal-delete-${s.submittal_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !uploading && setModalOpen(false)}
          data-testid="submittal-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-cyan-600">// UPLOAD</div>
                <div className="font-display font-black text-xl text-slate-900">New submittal</div>
              </div>
              <button
                onClick={() => !uploading && setModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
                disabled={uploading}
                data-testid="submittal-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Submittal type *">
                <select
                  value={form.doc_type}
                  onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                  className={inputCls}
                  data-testid="submittal-type-select"
                >
                  {Object.entries(types).map(([k, t]) => (
                    <option key={k} value={k}>{t.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="File * (PDF, PNG, JPG, HEIC · up to 15 MB)">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-cyan-100 file:text-cyan-700 file:font-mono file:font-bold file:text-xs file:tracking-widest file:cursor-pointer"
                  data-testid="submittal-file-input"
                />
                {file && (
                  <div className="text-xs text-slate-500 font-mono mt-1">
                    {file.name} · {(file.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </Field>

              {/* Show contextual fields based on selected type */}
              {["sds", "product_cut_sheet"].includes(form.doc_type) && (
                <>
                  <Field label="Product name">
                    <input
                      value={form.product_name}
                      onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                      className={inputCls}
                      placeholder="Duro-Last Elite Membrane"
                      data-testid="submittal-product-name"
                    />
                  </Field>
                  <Field label="Manufacturer">
                    <input
                      value={form.manufacturer}
                      onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                      className={inputCls}
                      placeholder="Duro-Last Inc."
                    />
                  </Field>
                </>
              )}
              {["osha_30", "osha_10", "cpr_first_aid"].includes(form.doc_type) && (
                <>
                  <Field label="Person name">
                    <input
                      value={form.person_name}
                      onChange={(e) => setForm({ ...form, person_name: e.target.value })}
                      className={inputCls}
                      placeholder="Michael Macias"
                      data-testid="submittal-person-name"
                    />
                  </Field>
                  <Field label="Expires (YYYY-MM-DD)">
                    <input
                      type="date"
                      value={form.expires_at}
                      onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                </>
              )}
              {["insurance", "bond", "sam_reg"].includes(form.doc_type) && (
                <Field label="Expires (YYYY-MM-DD)">
                  <input
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className={inputCls}
                  />
                </Field>
              )}
              <Field label="Scope tags (comma-separated)">
                <input
                  value={form.scope_tags}
                  onChange={(e) => setForm({ ...form, scope_tags: e.target.value })}
                  className={inputCls}
                  placeholder="roofing, membrane, sealants"
                />
              </Field>

              {form.doc_type === "signature" && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Photograph tips: dark ink on plain white paper, straight-on (not angled), even lighting, no
                  shadow across the ink. BidForge will flatten the paper texture, map ink to alpha, tight-crop,
                  and stamp the signature into every required signature block.
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => !uploading && setModalOpen(false)}
                disabled={uploading}
                className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800 px-4 py-2.5 disabled:opacity-40"
              >
                CANCEL
              </button>
              <button
                onClick={submit}
                disabled={uploading || !file}
                className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="submittal-submit"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? "UPLOADING" : "UPLOAD & INDEX"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Auto-fetch scraper modal */}
      {scraperOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !scraperBusy && setScraperOpen(false)}
          data-testid="scraper-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-cyan-600">// AUTO-FETCH</div>
                <div className="font-display font-black text-xl text-slate-900">Manufacturer document lookup</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Type the product and we'll try to grab the official SDS or spec PDF directly from the manufacturer.
                </div>
              </div>
              <button
                onClick={() => !scraperBusy && setScraperOpen(false)}
                className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
                disabled={scraperBusy}
                data-testid="scraper-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Document kind">
                <select
                  value={scraperState.kind}
                  onChange={(e) => setScraperState({ ...scraperState, kind: e.target.value })}
                  className={inputCls}
                  data-testid="scraper-kind"
                >
                  <option value="sds">Safety Data Sheet (SDS)</option>
                  <option value="cut_sheet">Product Data / Cut Sheet</option>
                </select>
              </Field>
              <Field label="Product name *">
                <input
                  value={scraperState.product_name}
                  onChange={(e) => setScraperState({ ...scraperState, product_name: e.target.value })}
                  className={inputCls}
                  placeholder="Duro-Last Elite Membrane"
                  data-testid="scraper-product"
                />
              </Field>
              <Field label="Manufacturer (helps narrow the search)">
                <input
                  value={scraperState.manufacturer}
                  onChange={(e) => setScraperState({ ...scraperState, manufacturer: e.target.value })}
                  className={inputCls}
                  placeholder="Duro-Last"
                  data-testid="scraper-manufacturer"
                />
              </Field>

              {scraperResult && !scraperResult.found && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="scraper-not-found">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-bold text-amber-900">Couldn't auto-fetch a PDF</div>
                      <div className="text-xs text-amber-800 mt-1">{scraperResult.reason}</div>
                      {scraperResult.search_links && scraperResult.search_links.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] font-mono font-bold tracking-widest text-amber-700 mb-1.5">
                            OPEN ONE-CLICK SEARCH
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {scraperResult.search_links.map((s) => (
                              <a
                                key={s.url}
                                href={s.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-mono font-bold tracking-widest bg-white border border-amber-300 text-amber-800 hover:border-amber-500 hover:bg-amber-100 rounded-full px-3 py-1.5"
                              >
                                <Search className="w-3 h-3" />
                                {s.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {scraperResult && scraperResult.found && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="scraper-found">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-bold text-emerald-900">
                        Auto-fetched "{scraperResult.submittal.title}"
                      </div>
                      <div className="text-xs text-emerald-700 mt-1 break-all">
                        Source: <a href={scraperResult.submittal.source_url} target="_blank" rel="noreferrer" className="underline">{scraperResult.submittal.source_url}</a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => !scraperBusy && setScraperOpen(false)}
                disabled={scraperBusy}
                className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800 px-4 py-2.5 disabled:opacity-40"
              >
                CLOSE
              </button>
              <button
                onClick={runScraper}
                disabled={scraperBusy || !scraperState.product_name.trim()}
                className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="scraper-fetch-btn"
              >
                {scraperBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {scraperBusy ? "SEARCHING" : "AUTO-FETCH"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-cyan-400 outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
